import { useQuery } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import Sort from "@components/common/inbox/Sort";
import { DSInput } from "@components/design-system/molecules";
import { DSTabs } from "@components/design-system/organisms";
import { cache } from "@components/index/providers";
import { OpenPipButton } from "@components/pip/OpenPipButton";
import {
  faFilter,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GET_INBOX_NOTES from "@graphql/queries/GetInboxNotes";
import GET_INBOX_TAB_COUNT from "@graphql/queries/GetInboxTabCount";
import { captureException } from "@sentry/react";
import { SEARCH_DEBOUNCE_TIME } from "@src/constants";
import { evictNoteCache } from "@src/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import InboxWindow from "./InboxWindow";
import NoteFilters from "./NoteFilters";
import { TAB_ARRAY } from "../../constants";
import {
  useDebouncedValue,
  useLocalStorage,
  useNoteMultiselectContext,
  useRecordingJobType,
  useWatchUserWsEvent,
} from "../../hooks";
import { useTeam } from "../../hooks/use-team";
import { MultiSelectActionContainer } from "../common/multiselect/MultiSelectActionContainer";
import PageTitle from "../layout/PageTitle";

export default function Inbox() {
  const { inboxSearch } = useFlags();

  const { control, register } = useForm({
    defaultValues: {
      searchQuery: "",
    },
  });

  const searchQuery = useWatch({ control, name: "searchQuery" });
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    SEARCH_DEBOUNCE_TIME,
  );

  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem("lastActiveTab");
    return savedTab !== null ? savedTab : "savedDrafts";
  });
  const activeTabObject = TAB_ARRAY.find(
    (tab) => tab.id === activeTab,
  );
  const {
    currentTeam,
    selectedTeamMembers,
    setSelectedTeamMembers,
    refreshUserTeams,
  } = useTeam();
  const { filterJobType, setFilterJobType } = useRecordingJobType();

  const [selectedRange, setSelectedRange] = useLocalStorage(
    "selected-date-range",
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  );
  const [sortAscending, setSortAscending] = useState(false);

  // Minimal state for NoteFilters compatibility (its writes are overridden in queryOptions)
  const [queryState, setQueryState] = useState({
    limit: 15,
    offset: 0,
  });

  const { resetSelectedNotesList } = useNoteMultiselectContext();

  // Derived query options — computed during render, no syncing effects needed
  const queryOptions = {
    ...queryState,
    filter: activeTabObject?.serverFilterKey,
    teamUuid: currentTeam?.uuid,
    userUuids: selectedTeamMembers,
    startDate: selectedRange.startDate,
    endDate: selectedRange.endDate,
    sortAscending,
    jobType: filterJobType?.value,
    searchQuery: debouncedSearchQuery,
  };

  // Declarative data fetching — Apollo refetches when queryOptions values change
  const {
    data: currentNotesData,
    previousData: previousNotesData,
    loading: notesLoading,
    refetch: notesRefetch,
    fetchMore,
  } = useQuery(GET_INBOX_NOTES, {
    variables: queryOptions,
    fetchPolicy: "network-only",
    onError: (error) => {
      captureException(error);
      alert("error", "Failed to load notes. Please try again.");
    },
  });

  // Keep showing previous data while new tab/filter loads (prevents flash)
  const notesData = currentNotesData ?? previousNotesData;

  const {
    data: currentTabCountData,
    previousData: previousTabCountData,
    refetch: tabCountRefetch,
  } = useQuery(GET_INBOX_TAB_COUNT, {
    variables: queryOptions,
    fetchPolicy: "network-only",
  });

  const tabCountData = currentTabCountData ?? previousTabCountData;

  const refetch = () => {
    evictNoteCache(cache);
    notesRefetch();
    tabCountRefetch();
  };

  useWatchUserWsEvent({
    onMessage: () => refetch(),
    eventName: "note_update",
  });

  // Apollo's mergePaginatedNotes type policy handles the cache merge
  const handleFetchMore = () => {
    return fetchMore({
      variables: {
        offset: notesData?.inboxNotes?.notes?.length || 0,
      },
    });
  };

  useEffect(() => {
    // "document.documentElement.scrollTo" is the magic for React Router Dom v6
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, []);

  const handleSort = () => {
    setSortAscending((prev) => !prev);
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <PageTitle
          title="Inbox"
          bottomAlignTitle={true}
          alignChildren="left"
        >
          <OpenPipButton
            pipUrl="/pip/notes"
            isBadgeEnabled={notesData?.inboxNotes?.notes?.length > 0}
          />
        </PageTitle>

        <div className="flex flex-row items-center space-x-1 md:flex-1 md:justify-end">
          <div className="flex flex-col">
            <div className="flex flex-row items-center">
              <FontAwesomeIcon
                icon={faFilter}
                className="text-secondary text-lg mr-1"
              />
              <NoteFilters
                searchQuery={debouncedSearchQuery}
                setQueryOptions={setQueryState}
                currentTeam={currentTeam}
                selectedTeamMembers={selectedTeamMembers}
                setSelectedTeamMembers={setSelectedTeamMembers}
                selectedJobType={filterJobType}
                setSelectedJobType={setFilterJobType}
                refreshCurrentTeam={refreshUserTeams}
                selectedRange={selectedRange}
                setSelectedRange={setSelectedRange}
              />
              <div className="pl-4">
                <Sort
                  sortAscending={sortAscending}
                  onSort={handleSort}
                />
              </div>
            </div>
            {inboxSearch && (
              <div className="w-full pt-2">
                <DSInput
                  leadingIcon={faSearch}
                  placeholder="Filter by searching..."
                  dataTestId="inbox-search"
                  {...register("searchQuery")}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <DSTabs
          value={activeTab}
          onChange={(tabId) => {
            if (tabId !== activeTab) {
              mixpanel.track("Switched Inbox Tab", {
                oldTab: activeTab,
                newTab: tabId,
              });
              resetSelectedNotesList();
              localStorage.setItem("lastActiveTab", tabId);
              setActiveTab(tabId);
            }
          }}
          dataTestId="inboxTabs"
        >
          <DSTabs.List>
            {TAB_ARRAY.map((tab) => (
              <DSTabs.Tab
                key={tab.id}
                value={tab.id}
                badge={tabCountData?.inboxTabCount?.[tab.countKey]}
                activeColor={tab.color}
              >
                {tab.title}
              </DSTabs.Tab>
            ))}
          </DSTabs.List>

          {TAB_ARRAY.map((tab) => (
            <DSTabs.Panel key={tab.id} value={tab.id}>
              <InboxWindow
                tabId={tab.id}
                tabTitle={tab.title}
                description={[tab.description]}
                activeTab={activeTab}
                isExportWindow={tab?.isExportWindow}
                isAlreadyExported={!!tab?.isAlreadyExported}
                data={notesData}
                refetch={refetch}
                fetchMore={handleFetchMore}
                notesLoading={notesLoading}
              />
            </DSTabs.Panel>
          ))}
        </DSTabs>
      </div>
      <MultiSelectActionContainer
        notes={notesData?.inboxNotes?.notes}
        refetch={refetch}
      />
    </>
  );
}
